import React, { useEffect, useState } from 'react';
import { Image, Upload, X } from 'lucide-react';
import { MediaPicker } from '../media/MediaPicker';
import type { MediaItem } from '../../types';
import {
  CREW_BOARD_HERO_SLOTS,
  CREW_BOARD_MEDIA,
  padCrewBoardHeroSlots,
  type CrewBoardPhoto,
} from '../../utils/crewJobsBoardMedia';
import {
  getSettings,
  mergePublicSettingsSubset,
} from '../../utils/appSettings';
import { ensurePortalPublicBrandingMediaItem } from '../../utils/portalBrandingStorage';
import { db } from '../../utils/firebase';
import { doc, setDoc } from 'firebase/firestore';

type PickerTarget = { kind: 'list'; index: number } | { kind: 'raise' };

const emptyHeroSlots = (): Array<CrewBoardPhoto | null> =>
  Array.from({ length: CREW_BOARD_HERO_SLOTS }, () => null);

const CrewBoardSettingsCard: React.FC = () => {
  const [listHeroes, setListHeroes] = useState<Array<CrewBoardPhoto | null>>(emptyHeroSlots);
  const [raiseHero, setRaiseHero] = useState<CrewBoardPhoto | null>(null);
  const [picker, setPicker] = useState<PickerTarget | null>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  useEffect(() => {
    void getSettings().then(settings => {
      setListHeroes(padCrewBoardHeroSlots(settings.crewBoardListHeroes, settings.crewBoardListHero));
      if (settings.crewBoardRaiseHero?.url) setRaiseHero(settings.crewBoardRaiseHero);
    });
  }, []);

  const applyPick = (item: MediaItem) => {
    const next = { url: item.url, name: item.name };
    if (picker?.kind === 'list') {
      setListHeroes(current => current.map((slot, i) => (i === picker.index ? next : slot)));
    } else if (picker?.kind === 'raise') {
      setRaiseHero(next);
    }
    setPicker(null);
  };

  const save = async () => {
    setSaving(true);
    setStatus('idle');
    try {
      const [resolvedSlots, resolvedRaise] = await Promise.all([
        Promise.all(listHeroes.map(slot => ensurePortalPublicBrandingMediaItem(slot))),
        ensurePortalPublicBrandingMediaItem(raiseHero),
      ]);
      setListHeroes(resolvedSlots);
      if (resolvedRaise !== raiseHero) setRaiseHero(resolvedRaise);
      const first = resolvedSlots.find(Boolean) || null;
      const patch = {
        crewBoardListHeroes: resolvedSlots,
        crewBoardListHero: first,
        crewBoardRaiseHero: resolvedRaise || null,
        updatedAt: new Date(),
      };
      await setDoc(doc(db, 'settings', 'app_settings'), patch, { merge: true });
      await mergePublicSettingsSubset(patch);
      setStatus('saved');
      window.setTimeout(() => setStatus('idle'), 2000);
    } catch (error) {
      console.error('Error saving raise-board photos:', error);
      setStatus('error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h4 className="settings-ops-section-title">Raise jobs board</h4>
      <p className="setting-description">
        Photos for the public <code>/crew-jobs</code> board. Leave the header empty to keep the
        default crane shot. Pick from the media library — we copy the file so contractors can see
        it without logging in.
      </p>

      <div className="setting-item">
        <h4>
          <Image size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Header photos
        </h4>
        <p className="setting-description">
          Up to three stills for the main board header. They fade into each other — no dots or
          controls. Empty slots are skipped.
        </p>
        <div className="crew-board-settings-slots">
          {listHeroes.map((photo, index) => (
            <PhotoSlot
              key={index}
              title={`Photo ${index + 1}`}
              hint={index === 0 ? 'Shows first, then fades to the next filled slot.' : 'Optional.'}
              photo={photo}
              fallbackSrc={index === 0 ? CREW_BOARD_MEDIA.hero : ''}
              fallbackLabel={index === 0 ? 'Default photo' : 'Empty'}
              onSelect={() => setPicker({ kind: 'list', index })}
              onClear={() => setListHeroes(current => current.map((slot, i) => (i === index ? null : slot)))}
            />
          ))}
        </div>
      </div>

      <div className="setting-divider" />

      <PhotoSlot
        title="Raising page photo"
        hint="Hero on each job page — 0032/ Oratory and any other raise."
        photo={raiseHero}
        fallbackSrc={CREW_BOARD_MEDIA.hero}
        fallbackLabel="Default photo"
        onSelect={() => setPicker({ kind: 'raise' })}
        onClear={() => setRaiseHero(null)}
      />

      <div style={{ display: 'flex', gap: 10, marginTop: 20, alignItems: 'center' }}>
        <button className="save-button" type="button" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : status === 'saved' ? 'Saved' : 'Save raise-board photos'}
        </button>
        {status === 'error' && (
          <span style={{ color: '#EF4444', fontSize: 13 }}>Could not save</span>
        )}
      </div>

      <MediaPicker
        isOpen={picker != null}
        onClose={() => setPicker(null)}
        allowedTypes={['image']}
        title={
          picker?.kind === 'raise'
            ? 'Raising page photo'
            : picker
              ? `Header photo ${picker.index + 1}`
              : 'Header photo'
        }
        onSelect={item => {
          if (picker) applyPick(item);
        }}
      />
    </>
  );
};

const PhotoSlot: React.FC<{
  title: string;
  hint: string;
  photo: CrewBoardPhoto | null;
  fallbackSrc: string;
  fallbackLabel: string;
  onSelect: () => void;
  onClear: () => void;
}> = ({ title, hint, photo, fallbackSrc, fallbackLabel, onSelect, onClear }) => (
  <div className="setting-item">
    <h4>{title}</h4>
    <p className="setting-description">{hint}</p>
    <div className={`crew-board-settings-preview${photo || fallbackSrc ? '' : ' is-empty'}`}>
      {photo?.url || fallbackSrc ? (
        <img src={photo?.url || fallbackSrc} alt="" />
      ) : (
        <div className="crew-board-settings-empty" />
      )}
      {!photo && <span className="crew-board-settings-fallback">{fallbackLabel}</span>}
    </div>
    {photo ? (
      <div className="selected-media-preview">
        <img src={photo.url} alt={photo.name} className="media-thumbnail" />
        <span className="media-name">{photo.name}</span>
        <button type="button" className="remove-media-btn" onClick={onClear} title="Remove">
          <X size={14} />
        </button>
      </div>
    ) : (
      <button type="button" className="select-media-btn" onClick={onSelect}>
        <Upload size={16} />
        Select photo
      </button>
    )}
    {photo && (
      <button type="button" className="select-media-btn" style={{ marginTop: 8 }} onClick={onSelect}>
        <Upload size={16} />
        Replace photo
      </button>
    )}
  </div>
);

export default CrewBoardSettingsCard;
